/**
 * The app shell: collapsing sidebar, topbar, account popover, quota footer.
 *
 * Metrics come from the prototype's inline styles — 264px sidebar collapsing to
 * 76px, 60px topbar, 38px nav rows at radius 10, quota colours switching at 75%
 * and 90%. Where the prototype and the token file disagree (the rail is 76px
 * here and `--app-rail: 56px` there) the prototype wins, since it is the pixel
 * reference the design was signed off on.
 *
 * **Tinted chrome on a panel layout**, decided 2026-09-09 and already shipping
 * in Snap and Suite. The window ground is violet (`--app-ground`); the sidebar
 * is a rounded panel of its own on it, one step lighter and still tinted
 * (`--app-chrome-panel`); the content is a neutral panel (`--app-panel`) at
 * `RADIUS.appPanel`; and `LAYOUT.gutter` of ground shows between them and
 * around the outside. The top bar sits on the ground with nothing under it —
 * the divider it used to draw is what made the window read as one flat grey.
 *
 * Two consequences worth knowing before moving anything:
 *
 *   · The top bar is still sticky, and it carries the top gutter itself
 *     (`paddingTop`, and that much extra height) rather than the container
 *     having a top padding. A sticky element cannot stop below its scroll
 *     container's padding, so with the padding on the container the panel
 *     would have scrolled through a 16px transparent band above the bar.
 *   · The sidebar is `position: sticky` at that same gutter with a viewport
 *     height minus both gutters, so it stays a panel rather than a column that
 *     runs off the bottom of the window.
 *
 * One row the prototype draws is still absent, because the feature behind it is
 * deferred: "Free up space". It is listed in docs/BACKLOG.md rather than
 * rendered disabled — a greyed row in a sidebar reads as a permissions problem
 * with the account rather than as an unbuilt feature.
 *
 * Workspaces *is* shown, to everyone, including accounts that cannot have one.
 * That is deliberate and the opposite call: the row leads to a screen that
 * explains what a workspace is and what it costs. Hiding it would mean a paid
 * feature nobody on the free plan ever discovers.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ds } from '../ds';
import { Link, useRouter } from '../router';
import { displayIdentity } from '@shared/handles';
import { useApp } from '../state';
import { entitlements } from '@shared/entitlements';
import { accountOrigin, signOutOfCloud } from '../pages/Vault';
import { Wordmark } from './Wordmark';
import { NewMenu } from './NewMenu';
import { NotificationBell } from './NotificationBell';
import { PresenceDot, usePresence } from './Presence';
import { setPresence } from '@shared/presence-client';
import { isSettablePresence, PRESENCE_STATES, type Presence } from '@shared/presence';
import {
  C,
  DUR,
  EASE,
  formatBytes,
  initials,
  LAYOUT,
  mono,
  monoPlain,
  RADIUS,
  storageTone,
  tableHead
} from '../theme';
import { fluid, hiddenColumns, useIsMobile, useIsNarrow, useWidth } from '../responsive';
import { useT } from '../i18n';
import { LocaleSwitch } from './LocaleSwitch';

interface NavItem {
  key: string;
  label: string;
  icon: string;
  href: string;
  /** An entitlement this destination needs. Absent means everyone gets it.
   *
   *  A gated item is **shown**, not hidden: greyed, with a diamond, and still a
   *  link — the page behind it sells the tier. Hiding it would mean nobody
   *  discovers the feature exists, which is the opposite of what a paid tier
   *  needs; a disabled row that does nothing when clicked is worse still,
   *  because it reads as a fault with the account. */
  requires?: 'auditLog' | 'organisation' | 'publishedCourses';
  /**
   * Hidden from a member of an organisation who does not administer it.
   *
   * Not the same rule as `requires`, and the difference is the whole reason it
   * is a second field. `requires` gates on a *tier*, and a gated row is shown
   * on purpose — the page behind it sells what has not been bought. This gates
   * on a *role* inside a tier already paid for, where there is nothing to sell
   * and the row is only an invitation to a screen the visitor cannot act on.
   *
   * Deliberately narrow: hidden only when there *is* an organisation and the
   * visitor is not its admin. An account with no organisation still sees it,
   * greyed, because for them it is an unbought tier and the sell branch on the
   * page is written for exactly that visitor.
   */
  adminOnly?: true;
}

/**
 * The rail.
 *
 * `label` is a catalogue key, not a string. It was ten English strings until
 * 2026-09-06, in an app where `Files.tsx` alone makes forty-two `t()` calls —
 * so somebody who had set their language got a fully translated page hanging
 * off a rail that still said "Trash". The fallback makes a missing translation
 * render the key, which is visible, so this cannot regress quietly.
 */
const NAV: NavItem[] = [
  { key: 'files', label: 'common.nav.files', icon: 'folder', href: '/' },
  { key: 'workspaces', label: 'common.nav.workspaces', icon: 'users', href: '/workspaces' },
  { key: 'shared-with', label: 'common.nav.shared-with', icon: 'inbox', href: '/shared' },
  { key: 'shared-by', label: 'common.nav.shared-by', icon: 'link-2', href: '/shared-by-me' },
  {
    key: 'courses',
    label: 'common.nav.courses',
    icon: 'graduation-cap',
    href: '/courses',
    requires: 'publishedCourses'
  },
  {
    key: 'audit',
    label: 'common.nav.audit',
    icon: 'scroll-text',
    href: '/audit',
    requires: 'auditLog'
  },
  {
    key: 'admin',
    label: 'common.nav.admin',
    icon: 'building-2',
    href: '/admin',
    requires: 'organisation',
    adminOnly: true
  },
  // After the sharing rows and before Trash: a computer's area is storage of
  // its own, not something shared, and not somewhere you go to undo.
  { key: 'computers', label: 'common.nav.computers', icon: 'monitor', href: '/computers' },
  { key: 'trash', label: 'common.nav.trash', icon: 'trash-2', href: '/trash' },
  { key: 'settings', label: 'common.nav.settings', icon: 'shield-check', href: '/settings' }
];

/**
 * Paths on mauvely.com, which is where the policies live.
 *
 * Cloud collects no analytics, so there is no consent control here and no
 * banner — the only non-essential storage in this app is the theme, and the
 * session cookie is strictly necessary. `Privacy settings` is still linked
 * because a Cloud user has a Mauvely account and may have set a level on
 * another site; the cookie is shared across the domain.
 */
const LEGAL_LINKS = [
  { label: 'Privacy', href: '/privacy' },
  { label: 'Cookies', href: '/cookies' },
  { label: 'Terms', href: '/terms' }
];

/** The lock on a gated nav row. A small diamond rather than a padlock: this is
 *  a tier marker, and a padlock reads as "you are not permitted", which is a
 *  different and more alarming statement. */
export const TierDiamond = ({ title }: { title: string }) => (
  <span
    title={title}
    aria-label={title}
    style={{
      marginLeft: 'auto',
      width: 13,
      height: 13,
      flex: '0 0 auto',
      display: 'block',
      background: 'currentColor',
      opacity: 0.5,
      clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
    }}
  />
);

/**
 * Is `path` this nav destination, or somewhere inside it?
 *
 * A bare `startsWith` is wrong here and was: `/shared-by-me` starts with
 * `/shared`, so standing on "Shared by me" lit up "Shared with me" as well and
 * both rows rendered selected. Matching on a segment boundary is the fix, and
 * reordering the nav would not have been — one href is a genuine prefix of the
 * other, so only the boundary distinguishes them.
 */
const isUnder = (path: string, href: string): boolean =>
  path === href || path.startsWith(`${href}/`);

/**
 * Whether the rail starts collapsed.
 *
 * A display preference like the theme, and stored the same way and for the same
 * reason — it reveals nothing, unlike anything key-shaped, which is why
 * `crypto/vault.ts` refuses localStorage outright and this does not hesitate.
 * It was `useState(false)` for a while, so the rail sprang back open on every
 * reload and on any navigation that remounted the shell.
 */
const RAIL_KEY = 'mauvely.cloud.rail-collapsed';

function initialCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    // Private mode, or storage blocked. Expanded is the right default.
    return false;
  }
}

export function Shell({ children }: { children: React.ReactNode }) {
  const t = useT();
  const { Icon, Progress } = ds();
  const { path } = useRouter();
  const { me, storage, theme, setTheme, lockVault } = useApp();
  const isMobile = useIsMobile();

  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(RAIL_KEY, collapsed ? '1' : '0');
    } catch {
      // Not worth surfacing — the rail still collapses for this session.
    }
  }, [collapsed]);

  const open = !collapsed && !isMobile;
  const width = isMobile ? 0 : open ? LAYOUT.sidebar : LAYOUT.rail;

  const percent = storage?.percent ?? 0;
  /** No ceiling: the bytes are billed rather than capped. -1 rather than a
   *  large number, so a client that has not been taught the sentinel shows
   *  something visibly wrong instead of a plausible wrong. */
  const unmetered = (storage?.limitMb ?? 0) < 0;

  const tone = storageTone(percent);
  const active = (item: NavItem) =>
    item.href === '/' ? path === '/' || isUnder(path, '/files') : isUnder(path, item.href);

  const G = LAYOUT.gutter;

  /**
   * The rows the rail actually draws, and which of them is lit.
   *
   * Computed here rather than inside the map because the marker below needs to
   * count in *rendered* rows: `adminOnly` removes one for a member of somebody
   * else's organisation, so an index into `NAV` would put the marker a row out
   * for exactly those accounts.
   */
  const visible = useMemo(
    () =>
      NAV.filter(
        (item) => !(item.adminOnly && me?.organisation && me.organisation.role !== 'admin')
      ),
    [me]
  );
  const activeIndex = visible.findIndex(active);

  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        background: 'var(--app-ground)',
        display: 'flex',
        gap: isMobile ? 0 : G,
        // No top padding: the top bar carries the top gutter, so that it can
        // stay stuck to the top of the window without a transparent band above
        // it for the content panel to scroll through.
        padding: isMobile ? 0 : `0 ${G}px ${G}px`
      }}
    >
      {!isMobile && (
        <aside
          style={{
            width,
            flex: `0 0 ${width}px`,
            background: 'var(--app-chrome-panel)',
            borderRadius: RADIUS.appPanel,
            display: 'flex',
            flexDirection: 'column',
            transition: `width ${DUR.base}ms ${EASE}`,
            position: 'sticky',
            marginTop: G,
            top: G,
            height: `calc(100vh - ${G * 2}px)`,
            overflow: 'hidden'
          }}
        >
          <div
            style={{
              height: LAYOUT.topbar,
              flex: `0 0 ${LAYOUT.topbar}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: open ? '0 14px 0 18px' : '0 8px 0 12px'
            }}
          >
            <Link to="/">
              <Wordmark collapsed={!open} showMark />
            </Link>
            <button
              aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
              onClick={() => setCollapsed((v) => !v)}
              style={iconButton(30)}
            >
              <Icon name={open ? 'panel-left-close' : 'panel-left-open'} size={17} />
            </button>
          </div>

          <NewMenu open={open} />

          <nav
            style={{
              position: 'relative',
              padding: open ? `${NAV_PAD}px 12px` : `${NAV_PAD}px 10px`,
              display: 'grid',
              gap: NAV_GAP
            }}
          >
            {/* The selected fill, drawn once and slid between rows rather than
                painted on whichever row happens to be active. Every row is the
                same height and the same distance apart, so where it belongs is
                arithmetic; the rows keep the label colour, which is the half of
                "selected" that cannot be shared.

                Hidden rather than unmounted when nothing matches — /recover and
                a 404 are both real paths with no row — so that arriving back on
                a nav destination fades it in where it lands instead of sliding
                it from row zero. */}
            <span
              aria-hidden
              style={{
                position: 'absolute',
                left: open ? 12 : 10,
                right: open ? 12 : 10,
                top: NAV_PAD,
                height: NAV_ROW,
                borderRadius: RADIUS.control,
                background: 'var(--primary-soft)',
                transform: `translateY(${Math.max(activeIndex, 0) * (NAV_ROW + NAV_GAP)}px)`,
                opacity: activeIndex < 0 ? 0 : 1,
                transition: `transform ${DUR.base}ms ${EASE}, opacity ${DUR.fast}ms ${EASE}`,
                pointerEvents: 'none'
              }}
            />
            {visible.map((item) => {
              // `publishedCourses` is a count and the others are booleans; zero and false
              // are both "not included", which is the only thing this needs to know.
              const locked = !!item.requires && !entitlements(me?.plan)[item.requires];
              return (
                <Link key={item.key} to={item.href}>
                  <span
                    className="mv-nav-row"
                    aria-current={active(item) ? 'page' : undefined}
                    style={{
                      ...navRow(active(item), open),
                      ...(locked ? { opacity: 0.55 } : {})
                    }}
                  >
                    <Icon name={item.icon} size={17} />
                    {open && <span>{t(item.label)}</span>}
                    {open && locked && (
                      <TierDiamond
                        title={item.requires === 'publishedCourses' ? 'Included with Max' : 'Included with Enterprise'}
                      />
                    )}
                  </span>
                </Link>
              );
            })}
          </nav>

          <div style={{ marginTop: 'auto' }}>
            {storage && (
              <div
                style={{
                  padding: open ? '18px 20px 20px' : '16px 10px',
                  borderTop: '1px solid var(--border-subtle)',
                  display: 'grid',
                  gap: 9
                }}
              >
                {open ? (
                  unmetered ? (
                    /* No ceiling, so there is no bar and no percentage — a
                       progress bar with nothing to fill reads as "nearly
                       empty", which is not what "no limit" means. An
                       organisation is billed for what it holds, and the upgrade
                       link is for people who can buy one. */
                    <span style={monoPlain}>{formatBytes(storage.usedBytes)} stored</span>
                  ) : (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={monoPlain}>
                          {formatBytes(storage.usedBytes)} of {formatBytes(storage.limitBytes)}
                        </span>
                        <span style={{ ...monoPlain, color: tone.color }}>{percent}%</span>
                      </div>
                      {/* The DS Progress reads --signal for its fill, so the
                          threshold colour is applied by overriding that variable
                          on the wrapper rather than by a prop it does not have. */}
                      <div style={{ ['--signal' as string]: tone.color }}>
                        <Progress value={percent} tone="signal" size="sm" />
                      </div>
                      {/* An organisation's members are allocated storage by
                          its administrator, and have nothing to buy. */}
                      {!me?.organisation && (
                        <a
                          href={`${accountOrigin()}/pricing`}
                          style={{ ...monoPlain, color: 'var(--text-link)', textDecoration: 'none' }}
                        >
                          {tone.action}
                        </a>
                      )}
                    </>
                  )
                ) : (
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 9,
                      display: 'grid',
                      placeItems: 'center',
                      margin: '0 auto',
                      background: 'var(--surface-sunken)',
                      color: tone.color
                    }}
                    title={
                      unmetered
                        ? `${formatBytes(storage.usedBytes)} stored`
                        : `${percent}% of storage used`
                    }
                  >
                    <Icon name="hard-drive" size={15} />
                  </div>
                )}
              </div>
            )}

            {/* Cloud has no footer, and this is the only bottom-of-chrome slot
                there is. Absolute links: the policies live on mauvely.com and
                are not restated here. Collapsed rail hides them rather than
                stacking three links in 26px. */}
            {open && (
              <div
                style={{
                  padding: '12px 20px 16px',
                  borderTop: '1px solid var(--border-subtle)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 12px'
                }}
              >
                {LEGAL_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={`${accountOrigin()}${l.href}`}
                    style={{ ...monoPlain, fontSize: 10.5, textDecoration: 'none' }}
                  >
                    {l.label}
                  </a>
                ))}
                {/* Beside the policies because this is the only bottom-of-chrome
                    slot there is. A choice made here is written to the cookie
                    mauvely.com reads, so it holds across the whole suite. */}
                <span style={{ flexBasis: '100%', paddingTop: 6 }}>
                  <LocaleSwitch />
                </span>
              </div>
            )}
          </div>
        </aside>
      )}

      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? 0 : G
        }}
      >
        <header
          style={{
            // The gutter above the bar is the bar's own padding, not the
            // container's — see the note at the top of this file.
            height: isMobile ? LAYOUT.topbar : LAYOUT.topbar + G,
            flex: '0 0 auto',
            paddingTop: isMobile ? 0 : G,
            // On the ground, with no divider. Opaque rather than transparent
            // because it is sticky and the content panel scrolls under it.
            background: 'var(--app-ground)',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingLeft: isMobile ? 16 : 8,
            paddingRight: isMobile ? 16 : 8,
            position: 'sticky',
            top: 0,
            zIndex: 4
          }}
        >
          {isMobile && (
            <Link to="/">
              <Wordmark collapsed={false} />
            </Link>
          )}

          <div style={{ flex: 1 }} />

          <NotificationBell />

          <button
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            style={iconButton(34)}
          >
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
          </button>

          <AccountChip
            open={menuOpen}
            onToggle={() => setMenuOpen((v) => !v)}
            onClose={() => setMenuOpen(false)}
            onLock={lockVault}
            name={me?.user.name ?? ''}
            // A provisioned account's address is derived and unroutable, so the
            // chip shows their handle instead. The Avatar's initials fall back
            // to it too, which is why one string is passed rather than two.
            email={me ? displayIdentity(me.user) : ''}
            avatarUrl={me?.user.avatarUrl ?? null}
          />
        </header>

        <main
          style={{
            flex: 1,
            background: 'var(--app-panel)',
            borderRadius: RADIUS.appPanel,
            // The gutter is the container's padding on a desktop window; on a
            // phone the column has none, so the panel insets itself.
            margin: isMobile ? '0 10px 10px' : 0,
            padding: isMobile ? '20px 16px 32px' : LAYOUT.contentPad
          }}
        >
          <PageFade path={path}>{children}</PageFade>
        </main>

        {isMobile && <MobileTabs path={path} />}
      </div>
    </div>
  );
}

// ── pieces ──────────────────────────────────────────────────────────────────

/**
 * The route cross-fade.
 *
 * A wrapper that replays a CSS animation, not a `key` on the outlet. Keying
 * would remount the page under it, and `/files/:a` → `/files/:b` renders the
 * same `Files` component both times — so every folder you opened would reset
 * the list/grid choice and re-run the mount effects. Removing the class,
 * reading a layout property to flush the removal, and adding it back is the
 * only way to restart an animation on an element that stays where it is.
 *
 * `prefers-reduced-motion` turns `.mv-page` into a fade with no rise, in
 * styles.css. It is deliberately not turned off: a fade with no movement is
 * what the readme asks for, and it is the only thing that says the view
 * changed when the chrome around it did not.
 */
function PageFade({ path, children }: { path: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove('mv-page');
    void el.offsetWidth;
    el.classList.add('mv-page');
  }, [path]);

  return (
    <div ref={ref} className="mv-page">
      {children}
    </div>
  );
}

/** The rail's row metrics. The marker slides in multiples of row + gap, so
 *  these are three constants rather than three literals in two places. */
const NAV_ROW = 38;
const NAV_GAP = 2;
const NAV_PAD = 4;

const navRow = (isActive: boolean, open: boolean): React.CSSProperties => ({
  height: NAV_ROW,
  display: 'flex',
  alignItems: 'center',
  justifyContent: open ? 'flex-start' : 'center',
  gap: 11,
  padding: open ? '0 12px' : 0,
  borderRadius: RADIUS.control,
  fontSize: 14,
  fontWeight: isActive ? 600 : 500,
  // The fill is the marker behind the rows, not this. `position: relative` is
  // what puts the label and icon in front of it — the marker is positioned and
  // would otherwise paint over static content.
  position: 'relative',
  background: 'transparent',
  color: isActive ? 'var(--primary-text)' : 'var(--text-body)',
  cursor: 'pointer',
  transition: 'var(--transition-control)'
});

const iconButton = (size: number): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: size >= 34 ? 11 : 10,
  display: 'grid',
  placeItems: 'center',
  border: 'none',
  background: 'transparent',
  color: 'var(--text-muted)',
  cursor: 'pointer'
});

function AccountChip({
  open,
  onToggle,
  onClose,
  onLock,
  name,
  email,
  avatarUrl
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onLock: () => void;
  name: string;
  email: string;
  avatarUrl: string | null;
}) {
  const { Icon } = ds();
  const ref = useRef<HTMLDivElement>(null);
  // Only here. `Avatar` is shared with the collaborator lists in Chat,
  // ShareDialog and Shared, and a dot inside it would claim to know the
  // presence of everybody it drew — this store holds one person's, the one
  // signed in.
  const { presence } = usePresence();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onEscape = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open, onClose]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 12px 4px 5px',
          borderRadius: 999,
          border: 'none',
          // Chrome on the ground rather than an inset control: the top bar has
          // no panel under it for a sunken tone to be sunken into.
          background: 'var(--app-chrome-panel)',
          cursor: 'pointer',
          fontSize: 13.5,
          color: 'var(--text-body)'
        }}
      >
        {/* The dot is `position: absolute`; this is what it is absolute
            inside. `ring` is the chip's own background, because the dot cuts
            itself out of whatever it sits on. */}
        <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
          <Avatar name={name} email={email} size={27} src={avatarUrl} />
          <PresenceDot presence={presence} size={9} ring="var(--app-chrome-panel)" />
        </span>
        <span>{name.split(' ')[0] || 'Account'}</span>
        <Icon name="chevron-down" size={14} />
      </button>

      {open && (
        <div
          className="mv-pop"
          style={{
            position: 'absolute',
            top: 44,
            right: 0,
            // Fixed at 272 it overflowed the right edge on a narrow phone, and
            // an absolutely positioned overflow does not scroll — it is simply
            // unreachable. The viewport minus the header's own padding wins
            // whenever it is smaller.
            width: 'min(272px, calc(100vw - 32px))',
            background: 'var(--surface-card)',
            borderRadius: 16,
            boxShadow: '0 18px 44px rgba(40,28,45,.18)',
            padding: 14,
            zIndex: 6
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <span style={{ position: 'relative', display: 'inline-flex', flex: 'none' }}>
              <Avatar name={name} email={email} size={36} src={avatarUrl} />
              {/* The popover's own background, not the chip's. */}
              <PresenceDot presence={presence} size={11} ring="var(--surface-card)" />
            </span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
                {name}
              </div>
              <div style={{ ...monoPlain, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {email}
              </div>
            </div>
          </div>

          <PresencePicker />

          <a
            href={`${accountOrigin()}/account`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 12px',
              borderRadius: 10,
              background: 'var(--primary-soft)',
              color: 'var(--primary-text)',
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            Account
            <Icon name="external-link" size={14} />
          </a>

          {/* Locking is Cloud's own action and separate from signing out:
              it drops the keys from memory without touching the session. */}
          {/* The bottom bar promotes four destinations and Trash is not one of
              them, so on a phone this menu is the only way to reach it. */}
          <Link to="/trash" style={{ display: 'block' }}>
            <span style={popoverRow}>
              <Icon name="trash-2" size={15} />
              Trash
            </span>
          </Link>

          <button onClick={onLock} style={popoverRow}>
            <Icon name="lock" size={15} />
            Lock files
          </button>
          {/* Sign-out belongs to mauvely.com, which owns the session cookie —
              clearing it here would leave the main site still signed in. */}
          <a
            href={`${accountOrigin()}/account`}
            onClick={(e) => {
              e.preventDefault();
              void signOutOfCloud();
            }}
            style={{ ...popoverRow, textDecoration: 'none' }}
          >
            <Icon name="log-out" size={15} />
            Sign out
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * What to tell other people, chosen here and stored on the account.
 *
 * Four pills rather than four popover rows: this menu already carries five
 * destinations, and five more rows would push Sign out off a phone.
 *
 * `PRESENCE_STATES.filter(isSettablePresence)` rather than a list of four —
 * `offline` and `unknown` are observations rather than choices, and the shared
 * module is the one place that says which is which.
 *
 * Clicking the chosen pill clears it, and so does Clear. Two ways because the
 * first is not discoverable and the second only appears once there is
 * something to clear.
 */
function PresencePicker() {
  const t = useT();
  const { override } = usePresence();

  const label: Record<Presence, string> = {
    available: t('common.presence.available'),
    away: t('common.presence.away'),
    busy: t('common.presence.busy'),
    dnd: t('common.presence.dnd'),
    // Never rendered — neither is offered.
    offline: '',
    unknown: ''
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
      {PRESENCE_STATES.filter(isSettablePresence).map((choice) => {
        const chosen = override === choice;
        return (
          <button
            key={choice}
            type="button"
            aria-pressed={chosen}
            onClick={() => void setPresence(chosen ? null : choice)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              padding: '5px 10px 5px 8px',
              borderRadius: 999,
              border: `1px solid ${chosen ? 'var(--primary)' : 'var(--border-subtle)'}`,
              background: chosen ? 'var(--primary-soft)' : 'transparent',
              color: chosen ? 'var(--primary-text)' : 'var(--text-body)',
              fontSize: 12.5,
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {/* The dot is `position: absolute`, so it needs something to be
                absolute inside even when it is not on an avatar. */}
            <span
              style={{ position: 'relative', display: 'inline-flex', width: 9, height: 9, flex: 'none' }}
            >
              <PresenceDot
                presence={choice}
                size={9}
                ring={chosen ? 'var(--primary-soft)' : 'var(--surface-card)'}
              />
            </span>
            {label[choice]}
          </button>
        );
      })}

      {override && (
        <button
          type="button"
          onClick={() => void setPresence(null)}
          style={{
            border: 0,
            background: 'none',
            padding: '5px 6px',
            cursor: 'pointer',
            fontSize: 12.5,
            fontWeight: 600,
            color: 'var(--text-link)'
          }}
        >
          {t('common.presence.clear')}
        </button>
      )}
    </div>
  );
}

/** Shared with `NewMenu`, so the three popovers in this app agree on a row. */
export const popoverRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  padding: '9px 12px',
  marginTop: 4,
  borderRadius: 10,
  border: 'none',
  background: 'transparent',
  color: 'var(--text-body)',
  fontSize: 14,
  cursor: 'pointer',
  textAlign: 'left'
};

export function Avatar({
  name,
  email,
  size = 27,
  src
}: {
  name: string;
  email: string;
  size?: number;
  /** The account's photo, served by website-main. Null for most accounts, and
   *  the initials chip below is not a placeholder for it — it is the answer. */
  src?: string | null;
}) {
  const shape = {
    width: size,
    height: size,
    flex: `0 0 ${size}px`,
    borderRadius: '50%'
  } as const;

  // Still no visual fallback: a photo that fails to load leaves the circle
  // empty rather than flashing initials in, and a broken avatar is a
  // website-main problem to see rather than one to paper over here.
  //
  // But it says so now. Without this the two reasons a circle is empty look
  // identical from the outside — no photo uploaded, which is normal and is what
  // the initials below are for, and a photo that 404s, which is not. The second
  // was reported as "Cloud doesn't show my avatar" and took a read of four
  // files across two repos to rule out, because nothing anywhere said a request
  // had failed.
  if (src) {
    return (
      <img
        src={src}
        alt=""
        style={{ ...shape, objectFit: 'cover' }}
        onError={() => {
          console.warn(
            `[avatar] ${src} did not load. The account has an avatar_key, so ` +
              'this is website-main not serving it — check that MEDIA is bound ' +
              'on that deployment.'
          );
        }}
      />
    );
  }

  return (
    <span
      style={{
        ...shape,
        display: 'grid',
        placeItems: 'center',
        background: C.avatarBg,
        color: C.avatarInk,
        fontSize: size * 0.4,
        fontWeight: 600
      }}
    >
      {initials(name, email)}
    </span>
  );
}

/** The prototype promotes four destinations to a bottom bar on mobile. */
function MobileTabs({ path }: { path: string }) {
  const { Icon } = ds();
  const tabs = [
    { label: 'Files', icon: 'folder', href: '/' },
    { label: 'Shared', icon: 'users', href: '/shared' },
    { label: 'Links', icon: 'link-2', href: '/shared-by-me' },
    { label: 'Keys', icon: 'shield-check', href: '/settings' }
  ];

  return (
    <nav
      style={{
        position: 'sticky',
        bottom: 0,
        display: 'flex',
        // Chrome, like the rail it stands in for — and with no divider, for the
        // same reason the top bar has none.
        background: 'var(--app-chrome-panel)',
        // 26px was a guess at the iPhone home indicator. `env()` is the real
        // figure, and it is 0 on a device that has no inset, so the bar does
        // not float above a flat-bottomed screen.
        padding: '8px 8px calc(10px + env(safe-area-inset-bottom, 16px))'
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.href === '/' ? path === '/' : isUnder(path, tab.href);
        return (
          <Link key={tab.href} to={tab.href} style={{ flex: 1 }}>
            <span
              aria-current={isActive ? 'page' : undefined}
              style={{
                display: 'grid',
                justifyItems: 'center',
                gap: 3,
                color: isActive ? 'var(--primary-text)' : 'var(--text-muted)',
                fontWeight: isActive ? 600 : 500,
                fontSize: 11
              }}
            >
              <Icon name={tab.icon} size={21} />
              {tab.label}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

// ── shared bits used by the pages ───────────────────────────────────────────

export const Panel = ({
  children,
  style
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => (
  <section
    style={{
      background: 'var(--surface-card)',
      borderRadius: 22,
      boxShadow: '0 1px 0 rgba(40,28,45,.04)',
      ...style
    }}
  >
    {children}
  </section>
);

export const PageTitle = ({ title, meta }: { title: string; meta?: string }) => (
  <div style={{ marginBottom: 4 }}>
    <h1
      style={{
        fontFamily: "var(--font-display), 'Baloo 2', system-ui",
        fontWeight: 800,
        // 40px is a third of a 360px viewport, so a two-word title wrapped to
        // three lines on a phone. `fluid` reaches its floor exactly at 360.
        fontSize: fluid(26, 40),
        lineHeight: 1.04,
        letterSpacing: '-.015em',
        color: 'var(--text-strong)',
        margin: 0
      }}
    >
      {title}
    </h1>
    {meta && <div style={{ ...monoPlain, marginTop: 8 }}>{meta}</div>}
  </div>
);

// ── the responsive table ────────────────────────────────────────────────────

/**
 * One table, two shapes.
 *
 * Every listing in the app is the same row — a type tile, a name, some
 * metadata, some actions — and each page had drawn it separately with hardcoded
 * pixel columns. Below about 640px that stops being a table: five fixed columns
 * in 360px squash the name to nothing. The `.mv-table` scroller in styles.css
 * was meant to cover this and never worked, because no component ever set the
 * class.
 *
 * So the row is a component instead, and it renders as a flex row on a wide
 * screen and a stacked card on a narrow one. Desktop metrics are unchanged;
 * the mobile branch is a different layout rather than a scaled one, which is
 * the distinction `responsive.ts` was written around.
 */
export interface Column {
  /** Header text. Empty for the tile and action columns. */
  label?: string;
  /** Fixed width in px, or 'flex' for the name column that takes the rest. */
  width: number | 'flex';
  align?: 'left' | 'right';
  /** Hide this column's label in the stacked layout — for values that already
   *  say what they are, or that repeat the subtitle. */
  hideOnNarrow?: boolean;
  /**
   * Drop the column when the *table* is narrower than this many pixels.
   *
   * Between desktop and phone there used to be one layout: every column at its
   * fixed width and the name taking whatever was left, which near the sidebar's
   * 900px collapse or just above the 600px stack was very little. Give this to
   * the columns a reader can do without, widest threshold first, and they leave
   * in that order. Never to the name or the action column. Ignored in the
   * stacked layout, which has room for every value.
   */
  hideBelow?: number;
}

/** The column indices the enclosing DataTable has no room for. Rows read it so
 *  their cells leave exactly where the header's labels do. */
const HiddenColumns = React.createContext<Set<number>>(new Set());

export function DataTable({
  columns,
  children,
  empty
}: {
  columns: Column[];
  children: React.ReactNode;
  empty?: React.ReactNode;
}) {
  const narrow = useIsNarrow();
  const [measure, width] = useWidth<HTMLDivElement>();
  const hidden = narrow ? NO_HIDDEN : hiddenColumns(columns, width);

  if (empty) return <Panel style={{ padding: '40px 34px' }}>{empty}</Panel>;

  return (
    // `minWidth: 0`, or a grid parent sizes this to its longest unbroken name
    // and the measured width never drops — see columns.test.ts.
    <div ref={measure} style={{ minWidth: 0 }}>
    <HiddenColumns.Provider value={hidden}>
    <Panel style={{ padding: narrow ? '6px 0' : '4px 0' }}>
      {/* A stacked card labels its own values, so the header is desktop-only.
          A strip inside a panel, so `--app-panel-alt` — the 2026-09-09 chrome
          decision. The 8px margin and 16px padding add up to the 24px the rows
          below are padded by, which is what keeps the headings over their own
          columns; change one and change the other. */}
      {!narrow && (
        <div
          style={{
            ...tableHead,
            display: 'flex',
            gap: 16,
            background: 'var(--app-panel-alt)',
            borderRadius: RADIUS.control,
            margin: '4px 8px 2px',
            padding: '10px 16px'
          }}
        >
          {columns.map((col, i) => hidden.has(i) ? null : (
            <span
              key={i}
              style={{
                // Matches DataRow's sizing exactly, or the headings drift out of
                // line with the values under them.
                ...(col.width === 'flex'
                  ? { flex: 1, minWidth: 0 }
                  : i === columns.length - 1
                    ? { minWidth: col.width, flex: '0 0 auto' }
                    : { flex: `0 0 ${col.width}px` }),
                textAlign: col.align ?? 'left'
              }}
            >
              {col.label ?? ''}
            </span>
          ))}
        </div>
      )}
      {children}
    </Panel>
    </HiddenColumns.Provider>
    </div>
  );
}

const NO_HIDDEN: Set<number> = new Set();

export function DataRow({
  columns,
  cells,
  tile,
  actions,
  first,
  onOpen,
  drag
}: {
  columns: Column[];
  /** One node per column *after* the tile and before the actions. The first is
   *  the name column and becomes the card's heading when stacked. */
  cells: React.ReactNode[];
  tile?: React.ReactNode;
  actions?: React.ReactNode;
  first?: boolean;
  onOpen?: () => void;
  /**
   * Makes the row draggable, a drop target, or both.
   *
   * Passed as one object rather than six props because the three pieces are
   * meaningless apart: a row that can be dragged but names nothing to drag, or
   * that accepts a drop and cannot say it is hovered, is a bug rather than a
   * configuration. Omitted entirely on rows that are neither — every row in the
   * trash, for one.
   */
  drag?: {
    /** Set on dragstart, read on drop. */
    id?: string;
    /** True while a valid drag is over this row. */
    over?: boolean;
    onDragStart?: () => void;
    onDragEnd?: () => void;
    onDragOver?: (e: React.DragEvent) => void;
    onDragLeave?: () => void;
    onDrop?: (e: React.DragEvent) => void;
  };
}) {
  const narrow = useIsNarrow();
  const hidden = React.useContext(HiddenColumns);

  // Row-to-row dragging is a pointer gesture with no touch equivalent, so it is
  // simply absent on a phone rather than approximated with a long-press that
  // would fight the scroll.
  const dragProps =
    drag && !narrow
      ? {
          draggable: !!drag.id,
          onDragStart: (e: React.DragEvent) => {
            if (!drag.id) return;
            // A payload the browser will actually carry: Firefox refuses to
            // start a drag with an empty dataTransfer.
            e.dataTransfer.setData('text/plain', drag.id);
            e.dataTransfer.effectAllowed = 'move';
            drag.onDragStart?.();
          },
          onDragEnd: drag.onDragEnd,
          onDragOver: drag.onDragOver,
          onDragLeave: drag.onDragLeave,
          onDrop: drag.onDrop
        }
      : {};

  const dropTint = drag?.over
    ? { background: 'var(--primary-soft)', outline: '1px solid var(--primary)' }
    : {};
  // Columns line up with `cells`, minus the leading tile and trailing actions.
  const valueColumns = columns.slice(tile ? 1 : 0, actions ? -1 : undefined);
  const border = first ? 'none' : '1px solid var(--border-subtle)';

  if (narrow) {
    const [name, ...rest] = cells;
    return (
      <div
        className="mv-row"
        style={{ padding: '13px 18px', borderTop: border, display: 'grid', gap: 10, ...dropTint }}
        {...dragProps}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: onOpen ? 'pointer' : undefined }}
          onClick={onOpen}
        >
          {tile}
          <div style={{ flex: 1, minWidth: 0 }}>{name}</div>
        </div>

        {rest.some(Boolean) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
            {rest.map((cell, i) => {
              const col = valueColumns[i + 1];
              if (!cell || col?.hideOnNarrow) return null;
              return (
                <span key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  {col?.label && <span style={tableHead}>{col.label}</span>}
                  <span>{cell}</span>
                </span>
              );
            })}
          </div>
        )}

        {actions && (
          // Left-aligned and full-height on a phone: a 32px control pinned to
          // the right edge is both hard to hit and hard to see.
          <div style={{ display: 'flex', gap: 4, marginLeft: -6 }}>{actions}</div>
        )}
      </div>
    );
  }

  return (
    <div
      className="mv-row"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '11px 24px',
        borderTop: border,
        cursor: onOpen ? 'pointer' : undefined,
        ...dropTint
      }}
      // One click, anywhere on the row.
      //
      // It used to be a double click on the row and a single click on the name
      // cell alone, which is two rules and neither of them visible: the pointer
      // cursor is on the whole row, so a click on the size column looks like it
      // should do what a click on the name does, and instead did nothing at
      // all — not even select, so there was no feedback to learn from. The
      // narrow layout above has always opened on a single click of the row;
      // this makes the wide one agree with it.
      //
      // `onOpen` navigates for a folder and opens a preview for a file. Both
      // are reversible, which is what makes a single click the right weight.
      onClick={
        onOpen
          ? (e) => {
              // A click on a control inside the row belongs to that control.
              if (
                (e.target as HTMLElement).closest(
                  'button, a, input, select, textarea, label, [role="button"]'
                )
              )
                return;
              onOpen();
            }
          : undefined
      }
      {...dragProps}
    >
      {tile}
      {cells.map((cell, i) => {
        const col = valueColumns[i];
        // Indexed against the full column list, which is what the header hid.
        if (hidden.has(i + (tile ? 1 : 0))) return null;
        return (
          <span
            key={i}
            style={{
              // `flex: 0 0 <w>` rather than a bare width: a plain width is only
              // a basis, so a fixed column would still shrink under pressure
              // from the row's own content.
              ...(col?.width === 'flex'
                ? { flex: 1, minWidth: 0 }
                : { flex: `0 0 ${col?.width ?? 0}px` }),
              textAlign: col?.align ?? 'left'
            }}
          >
            {cell}
          </span>
        );
      })}
      {actions && (
        <span
          style={{
            // The action column's declared width is a floor, not a cap. Four
            // 32px buttons need 140px, and pinning the cell at 100 let them
            // spill left over the column beside it.
            minWidth: columns[columns.length - 1]?.width as number,
            flex: '0 0 auto',
            display: 'flex',
            gap: 4,
            justifyContent: 'flex-end'
          }}
        >
          {actions}
        </span>
      )}
    </div>
  );
}

export const Eyebrow = ({ children }: { children: React.ReactNode }) => (
  <div style={mono}>{children}</div>
);

/** The one-line reassurance the prototype puts under anything destructive. */
export const Footnote = ({ children }: { children: React.ReactNode }) => (
  <p style={{ fontSize: 13.5, color: 'var(--text-muted)', margin: '14px 0 0', lineHeight: 1.55 }}>
    {children}
  </p>
);
